import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OrganizationsPanel, AddMarketDialog } from "@/components/mai/AdminForms";
import { Buildings, Stack, Cube, Globe, Graph, Database, UserPlus, X, GlobeHemisphereEast } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAYER_COLORS = {
  knowledge: "#1D4ED8", intelligence: "#047857", decision: "#B45309",
  application: "#4338CA", experience: "#0F766E",
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-5 border border-slate-200 bg-white flex items-start gap-3">
    <Icon size={20} className="text-slate-400 mt-0.5" />
    <div>
      <p className="font-heading font-extrabold text-2xl leading-none">{value}</p>
      <p className="overline-label text-slate-400 mt-1.5">{label}</p>
    </div>
  </div>
);

const UserAccessPanel = () => {
  const [users, setUsers] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const load = () => axios.get(`${API}/auth/users`).then((r) => setUsers(r.data)).catch(() => setUsers(null));
  useEffect(() => { load(); }, []);

  if (users === null) return null;

  const add = async () => {
    setError("");
    try {
      await axios.post(`${API}/auth/users`, { email, role: "member" });
      setEmail(""); load();
    } catch (e) { setError(e.response?.data?.detail || "Failed to add"); }
  };
  const remove = async (em) => {
    setError("");
    try { await axios.delete(`${API}/auth/users/${encodeURIComponent(em)}`); load(); }
    catch (e) { setError(e.response?.data?.detail || "Failed to remove"); }
  };

  return (
    <section data-testid="user-access-panel" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="font-heading font-bold text-sm tracking-tight">User Access — Admin</p>
        <p className="text-xs text-slate-500 mt-1">Only approved emails can sign in (Google or email code).</p>
      </div>
      <div className="px-5 py-4 border-b border-slate-100 flex gap-2">
        <Input data-testid="add-user-email-input" type="email" placeholder="person@company.com" value={email}
          onChange={(e) => setEmail(e.target.value)} className="rounded-none h-9 text-sm"
          onKeyDown={(e) => e.key === "Enter" && email && add()} />
        <Button data-testid="add-user-btn" onClick={add} disabled={!email}
          className="bg-slate-900 text-white hover:bg-slate-700 rounded-none h-9 gap-1.5 shrink-0">
          <UserPlus size={14} /> Approve
        </Button>
      </div>
      {error && <p data-testid="user-access-error" className="px-5 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">{error}</p>}
      <div className="divide-y divide-slate-100">
        {users.map((u) => (
          <div key={u.email} data-testid={`allowed-user-${u.email}`} className="px-5 py-2.5 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 truncate">{u.email}</p>
              <p className="text-[10px] text-slate-400">{u.role}{u.added_by ? ` · added by ${u.added_by}` : ""}</p>
            </div>
            {u.role !== "admin" && (
              <button data-testid={`remove-user-${u.email}`} onClick={() => remove(u.email)}
                className="shrink-0 text-slate-300 hover:text-red-600 p-1" title="Revoke access">
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default function PlatformPage() {
  const [meta, setMeta] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [products, setProducts] = useState([]);
  const [marketDialog, setMarketDialog] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/platform/meta`),
      axios.get(`${API}/admin/organizations`),
      axios.get(`${API}/admin/workspaces`),
      axios.get(`${API}/products`),
    ]).then(([m, o, w, p]) => {
      setMeta(m.data); setOrgs(o.data); setWorkspaces(w.data); setProducts(p.data);
    }).catch(console.error);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!meta) return (
    <PageShell pageId="platform" overline="Administration Engine" title="Platform Administration">
      <div data-testid="platform-loading" className="py-32 text-center text-slate-400 text-sm">Loading platform metadata…</div>
    </PageShell>
  );

  const c = meta.counts;
  return (
    <PageShell pageId="platform" overline="Administration Engine · Platform First, Product Second, Client Third" title="Platform Administration"
      actions={
        <button data-testid="add-market-btn" onClick={() => setMarketDialog(true)}
          className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center gap-2">
          <GlobeHemisphereEast size={14} /> Add Market
        </button>
      }>
      <div data-testid="platform-stats" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <Stat icon={Buildings} label="Organizations" value={c.organizations} />
        <Stat icon={Cube} label="Workspaces" value={c.workspaces} />
        <Stat icon={Stack} label="Products" value={c.products} />
        <Stat icon={Globe} label="Country Intelligence" value={c.country_intelligence} />
        <Stat icon={Database} label="Evidence Assets" value={c.evidence_assets} />
        <Stat icon={Graph} label="Graph Nodes / Edges" value={`${c.kg_nodes} / ${c.kg_edges}`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section data-testid="platform-layers" className="xl:col-span-2 bg-white border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-200">
            <p className="font-heading font-bold text-sm tracking-tight">Five Immutable Platform Layers</p>
            <p className="text-xs text-slate-500 mt-1">Every future module plugs into the same intelligence backbone.</p>
          </div>
          {meta.layers.map((layer) => {
            const engines = meta.engines.filter((e) => e.layer === layer.id);
            const color = LAYER_COLORS[layer.id];
            return (
              <div key={layer.id} data-testid={`layer-${layer.id}`} className="px-5 py-4 border-b border-slate-100 last:border-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono-data text-[10px] text-slate-400">L{layer.order}</span>
                  <p className="font-heading font-bold text-sm" style={{ color }}>{layer.name}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{layer.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {engines.map((e) => (
                    <span key={e.id} data-testid={`engine-${e.id}`} title={e.description}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 border font-semibold"
                      style={{ color, borderColor: `${color}44`, background: `${color}0A` }}>
                      {e.name}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <div className="space-y-6">
          <UserAccessPanel />
          <OrganizationsPanel orgs={orgs} onChanged={load} />
          <section data-testid="platform-tenancy" className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <p className="font-heading font-bold text-sm tracking-tight">Tenancy — Organizations & Workspaces</p>
            </div>
            {orgs.map((org) => (
              <div key={org.id} className="px-5 py-4 border-b border-slate-100 last:border-0">
                <p className="text-sm font-semibold text-slate-900">{org.name}
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-2">{org.type}</span>
                </p>
                {workspaces.filter((w) => w.organization_id === org.id).map((w) => (
                  <div key={w.id} className="mt-2 ml-3 pl-3 border-l-2 border-slate-200">
                    <p className="text-xs font-semibold text-slate-700">{w.name}</p>
                    {products.filter((p) => p.workspace_id === w.id).map((p) => (
                      <p key={p.id} className="text-xs text-slate-500 mt-1">— {p.name} <span className="text-slate-400">({p.category})</span></p>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </section>

          <section data-testid="platform-principles" className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <p className="font-heading font-bold text-sm tracking-tight">Platform Principles</p>
            </div>
            <ul className="px-5 py-4 space-y-2.5">
              {meta.principles.map((p, i) => (
                <li key={i} className="text-xs text-slate-600 leading-relaxed flex gap-2">
                  <span className="text-slate-300 font-mono-data shrink-0">{String(i + 1).padStart(2, "0")}</span>{p}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
      {marketDialog && <AddMarketDialog onClose={() => setMarketDialog(false)} onCreated={() => { setMarketDialog(false); load(); }} />}
    </PageShell>
  );
}
